import React from 'react';
import { ArrowRight, CheckCircle2, BarChart, Users, Clock, Award, Server } from 'lucide-react';

const CaseStudies = () => {
  const caseStudies = [
    {
      title: "Custom CRM Implementation",
      client: "Local Real Estate Agency",
      industry: "Real Estate",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
      challenge: "Managing client relationships with spreadsheets and email",
      solution: "Lightweight, customized CRM system with property tracking",
      results: [
        "2x faster lead response time",
        "Eliminated double-entry of data",
        "Improved client follow-up by 80%",
        "Centralized property listings"
      ],
      technologies: ["React", "Node.js", "PostgreSQL", "Supabase"]
    },
    {
      title: "Inventory Management System",
      client: "Boutique Retail Store",
      industry: "Retail",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80",
      challenge: "Manual inventory tracking leading to stockouts",
      solution: "Simple, mobile-friendly inventory system",
      results: [
        "Reduced stockouts by 90%",
        "15 hours saved per week",
        "Real-time inventory updates",
        "Improved order accuracy"
      ],
      technologies: ["React", "TypeScript", "Express", "SQLite"]
    },
    {
      title: "Cost-Effective SaaS Hosting",
      client: "EdTech Startup",
      industry: "Education",
      image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=800&q=80",
      challenge: "High cloud hosting costs limiting startup growth",
      solution: "Migration to our optimized infrastructure with dedicated resources",
      results: [
        "Reduced hosting costs by 65%",
        "Improved application performance",
        "99.99% uptime achieved",
        "Scalable infrastructure"
      ],
      technologies: ["Bare Metal Servers", "Docker", "Nginx", "PostgreSQL"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Success Stories</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              See how we've helped local businesses streamline their operations and grow through custom software solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <div key={index} className="grid lg:grid-cols-2 gap-12 items-center">
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img
                    src={study.image}
                    alt={study.title}
                    className="rounded-xl shadow-2xl w-full"
                  />
                </div>
                <div>
                  <div className="text-yellow-400 font-semibold mb-2">{study.industry}</div>
                  <h2 className="text-3xl font-bold mb-4">{study.title}</h2>
                  <div className="text-xl text-gray-600 mb-6">{study.client}</div>
                  
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Users className="w-5 h-5 text-yellow-400 mr-2" />
                        Challenge
                      </h3>
                      <p className="text-gray-600">{study.challenge}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Award className="w-5 h-5 text-yellow-400 mr-2" />
                        Solution
                      </h3>
                      <p className="text-gray-600">{study.solution}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <BarChart className="w-5 h-5 text-yellow-400 mr-2" />
                        Key Results
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {study.results.map((result, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                            <span className="text-gray-600">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Clock className="w-5 h-5 text-yellow-400 mr-2" />
                        Technologies Used
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {study.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure Advantage Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Server className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-6">Our Infrastructure Advantage</h2>
              <p className="text-lg text-gray-600">
                We leverage our own enterprise-grade infrastructure to provide cost-effective, 
                high-performance hosting solutions for our clients.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Cost Savings",
                  description: "Up to 65% reduction in hosting costs compared to major cloud providers"
                },
                {
                  title: "Performance",
                  description: "Dedicated resources ensuring consistent, high-performance operation"
                },
                {
                  title: "Reliability",
                  description: "99.99% uptime with redundant systems and 24/7 monitoring"
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Let's Build Your Success Story</h2>
            <p className="text-xl text-gray-300 mb-8">
              We're ready to help you create a custom solution that transforms your business operations.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;